import {CSSProperties, useMemo} from "react"
import firstStepsData from "@/lib/copy/pathways/firstSteps"
import type {UserSuccessPathway} from "@/lib/server/models/user"
import {roundDecimal} from "@/lib/utils"
import {usePathwayData, UserPathwayPage} from "@/lib/hooks/usePathwayData"

export type UserProgressLineProps = {
  animate?: boolean
  uiPrefs: { key: string }[]
  userSuccessPathway: UserSuccessPathway | (string & {})
}

const FIRST_WIN_HASH = "first-steps:lead"
const SECOND_WIN_HASH_TAIL = "gpPAZMNc"
const THIRD_WIN_HASH_TAIL = "uJuKrbGv"
const EVERGREEN_PLAN_HASH_TAIL = "updateEvergreenPlan"

export default function UserPathwayProgressLine(
  props: UserProgressLineProps
): JSX.Element {
  const pathwayData = usePathwayData(props.userSuccessPathway)
  
  const stats = useMemo(() => {
    let totalTasks = firstStepsData.items.length
    let completed = props.uiPrefs.reduce(
      (t, next) => t + (next.key.startsWith("first-steps") ? 1 : 0),
      0
    )
    if (!pathwayData || props.userSuccessPathway === "first-steps") {
      const percent = (completed / totalTasks) * 100
      return {
        totalTasks,
        completed,
        completePercentage: roundDecimal(percent, 2),
        todoPercentage: roundDecimal(100 - percent, 2),
      }
    }

    const pathwayTotal = pathwayData.reduce(
      (t, next) => t + next.items.length,
      0
    )
    totalTasks += pathwayTotal
    const pathwayComplete = props.uiPrefs.reduce(
      (t, next) => t + (next.key.startsWith(props.userSuccessPathway) ? 1 : 0),
      0
    )
    completed += pathwayComplete
    const rawPathPercent = (completed / totalTasks) * 100
    let animateFactor = 0
    if (rawPathPercent < 90 && props.animate) {
      animateFactor = 4
    }
    const pathPercent = rawPathPercent + animateFactor
    const isBetween0and1Percent = (
      pathPercent > 0 && pathPercent < 1
    )
    if (isBetween0and1Percent) {
      return {
        totalTasks,
        completed,
        completePercentage: 1,
        todoPercentage: 99,
      }
    }
    return {
      totalTasks,
      completed,
      completePercentage: roundDecimal(pathPercent, 2),
      todoPercentage: roundDecimal(100 - pathPercent, 2),
    }
  }, [props.uiPrefs, pathwayData, props.animate])

  const bigWins = useMemo(() => {
    const win1 = props.uiPrefs.some(
      (p) => p.key === FIRST_WIN_HASH
    )
    const win2 = props.uiPrefs.some(
      (p) => p.key.endsWith(SECOND_WIN_HASH_TAIL)
    )
    const win3 = props.uiPrefs.some(
      (p) => p.key.endsWith(THIRD_WIN_HASH_TAIL)
    )
    const evergreenPlan = props.uiPrefs.some(
      (p) => p.key.endsWith(EVERGREEN_PLAN_HASH_TAIL)
    )
    return {win1, win2, win3, evergreenPlan}
  }, [props.uiPrefs])

  return <div 
    className={`flex relative z-0 w-full ${props.animate ? "animate-bounce" : ""}`}
  >
    <div 
      className={`mt-2 ${props.animate ? "animate-pulse bg-purple-400" : "bg-green-400"}`}
      style={{
        width: `${stats.completePercentage}%`,
        height: "0.5rem"
      }}
    />
    <div 
      className="mt-2 bg-gray-400"
      style={{
        width: `${stats.todoPercentage}%`,
        height: "0.5rem"
      }}  
    />

    {pathwayData ? <BigWinIndicators
      bigWins={bigWins}
      userPages={pathwayData}
      animate={props.animate}
    /> : null}
  </div>
}

type BigWinRecord = {
  win1: boolean
  win2: boolean
  win3: boolean
  evergreenPlan: boolean
}

type IndicatorMeta = Readonly<{
  key: keyof BigWinRecord
  title: string 
  style: CSSProperties 
}>

const INDICATOR_META: readonly IndicatorMeta[] = [
  {key: "win1", title: "Big Win #1: First Lead", style: {marginLeft: "10%"}},
  {key: "win2", title: "Big Win #2: First Bribe Call", style: {marginLeft: "10%"}},
  {key: "win3", title: "Big Win #3: Successful Pre-Qualifications", style: {marginLeft: "30%"}},
  {key: "evergreenPlan", title: "Evergreen Plan Created", style: {right: "-5px", top: "0px", position: "absolute"}},
]

type BigWinIndicatorsProps = {
  bigWins: BigWinRecord
  userPages: UserPathwayPage[]
  animate?: boolean
}

function BigWinIndicators(
  props: BigWinIndicatorsProps
): JSX.Element {
  return <div 
    className="absolute z-10 flex w-full"
    style={{top: "2px"}}
  >
    {INDICATOR_META.map((meta) => <div 
      key={`win-indicator-${meta.key}`}
      className={`rounded-full h-5 w-5 hover:bg-ppt-blue ${props.bigWins[meta.key] ? (props.animate ? "bg-purple-400" : "bg-green-400") : "bg-gray-400"}`}
      title={meta.title}
      style={meta.style}
    />)}
  </div>
}